import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Mail, ArrowLeft, Send, CheckCircle2, Loader2, MapPin, Clock, Phone } from "lucide-react";
import { VENUE } from "../lib/venue";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = () => ({
  first_name: "",
  last_name: "",
  postcode: "",
  email: "",
  mobile: "",
});

export default function Join() {
  const [form, setForm] = useState(empty());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await axios.post(`${API}/subscribers`, form);
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const bind = (key) => ({
    value: form[key],
    onChange: (e) => setForm({ ...form, [key]: e.target.value }),
  });

  return (
    <div
      className="grain min-h-screen bg-[#0A0A0A] text-[#FDFBF7] flex flex-col"
      data-testid="join-page"
    >
      {/* Simple top bar */}
      <header className="border-b border-white/10 sticky top-0 bg-black/85 backdrop-blur z-10">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="join-back-home">
            <img
              src={VENUE.logoUrl}
              alt="The Bar Colac logo"
              className="h-12 w-12 object-contain"
            />
            <span className="font-display text-2xl tracking-wider text-[#FDFBF7] hidden sm:inline">
              THE BAR
            </span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#FDFBF7]/70 hover:text-[#F5A623]"
            data-testid="join-back-site"
          >
            <ArrowLeft size={14} /> Back to site
          </Link>
        </div>
      </header>

      <main className="flex-1 py-16 sm:py-24 px-6 sm:px-10">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-[1.05fr_1fr] gap-16 items-start">
          {/* Left — pitch */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-3 mb-6">
              <span className="h-px w-12 bg-[#F5A623]" />
              <span className="text-xs uppercase tracking-[0.3em] text-[#F5A623] font-semibold">
                Join the crew
              </span>
            </div>

            <h1
              className="font-display text-5xl sm:text-6xl lg:text-7xl text-[#FDFBF7] uppercase leading-[0.9]"
              data-testid="join-heading"
            >
              Never miss a
              <br />
              <span className="text-[#F5A623]">good night.</span>
            </h1>

            <p className="mt-6 text-lg text-[#FDFBF7]/75 leading-relaxed max-w-lg">
              Sign up and be the first to hear about live gigs, karaoke themes,
              drink specials and anything else worth turning up for. No spam —
              just the good stuff, straight from The Bar.
            </p>

            <div className="mt-10 space-y-4 text-[#FDFBF7]/70 text-sm">
              <div className="flex items-center gap-3">
                <MapPin size={16} className="text-[#F5A623]" />
                <span>{VENUE.address}</span>
              </div>
              <div className="flex items-center gap-3">
                <Clock size={16} className="text-[#F5A623]" />
                <span>{VENUE.hours}</span>
              </div>
              <div className="flex items-center gap-3">
                <Mail size={16} className="text-[#F5A623]" />
                <a
                  href={`mailto:${VENUE.email}`}
                  className="hover:text-[#F5A623] transition-colors"
                >
                  {VENUE.email}
                </a>
              </div>
            </div>
          </motion.div>

          {/* Right — form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="relative"
          >
            <div className="absolute -inset-4 bg-[#F5A623]/10 blur-3xl pointer-events-none" />

            {done ? (
              <div
                className="relative bg-[#111114] border border-[#F5A623]/60 p-8 sm:p-10 shadow-[8px_8px_0px_0px_rgba(245,166,35,0.35)]"
                data-testid="join-success"
              >
                <CheckCircle2 size={40} className="text-[#F5A623] mb-4" />
                <h2 className="font-display text-3xl uppercase text-[#FDFBF7] mb-2">
                  You're in.
                </h2>
                <p className="text-[#FDFBF7]/70 mb-8 leading-relaxed">
                  Cheers for signing up — keep an eye on your inbox for the
                  next event lineup. We'll only reach out when it matters.
                </p>
                <Link
                  to="/"
                  data-testid="join-success-back"
                  className="inline-flex items-center gap-2 bg-[#F5A623] hover:bg-amber-400 text-black font-bold uppercase tracking-wider px-6 py-3 transition-colors"
                >
                  <ArrowLeft size={16} /> Back to the site
                </Link>
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="join-form"
                className="relative bg-[#111114] border border-[#F5A623]/40 p-6 sm:p-10 shadow-[8px_8px_0px_0px_rgba(245,166,35,0.25)]"
                noValidate
              >
                <h2 className="font-display text-2xl uppercase text-[#FDFBF7] mb-1">
                  Sign me up
                </h2>
                <p className="text-sm text-[#FDFBF7]/55 mb-8">
                  Takes 20 seconds. Free forever.
                </p>

                <div className="grid sm:grid-cols-2 gap-4">
                  <Field
                    label="First name"
                    testid="join-first-name"
                    required
                    autoComplete="given-name"
                    {...bind("first_name")}
                  />
                  <Field
                    label="Last name"
                    testid="join-last-name"
                    required
                    autoComplete="family-name"
                    {...bind("last_name")}
                  />
                </div>
                <div className="mt-4">
                  <Field
                    label="Postcode"
                    testid="join-postcode"
                    required
                    autoComplete="postal-code"
                    inputMode="numeric"
                    pattern="\d{4}"
                    maxLength={4}
                    placeholder="3250"
                    {...bind("postcode")}
                  />
                </div>
                <div className="mt-4">
                  <Field
                    label="Email address"
                    testid="join-email"
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="you@example.com"
                    {...bind("email")}
                  />
                </div>
                <div className="mt-4">
                  <Field
                    label="Mobile number"
                    testid="join-mobile"
                    type="tel"
                    required
                    autoComplete="tel"
                    inputMode="tel"
                    placeholder="0400 000 000"
                    {...bind("mobile")}
                    icon={<Phone size={14} className="text-[#F5A623]" />}
                  />
                </div>

                <p
                  data-testid="join-disclaimer"
                  className="mt-8 text-xs text-[#FDFBF7]/55 leading-relaxed border-l-2 border-[#F5A623]/60 pl-4"
                >
                  By submitting your details, you agree to receive emails and
                  SMS messages from The Bar Colac about upcoming events, live
                  music, karaoke nights and drink specials. You can unsubscribe
                  at any time.
                </p>

                {error && (
                  <p
                    data-testid="join-error"
                    className="mt-4 text-sm text-red-400 border border-red-400/30 bg-red-400/10 px-3 py-2"
                  >
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="join-submit"
                  className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-[#F5A623] hover:bg-amber-400 disabled:opacity-60 text-black font-bold uppercase tracking-wider text-sm px-6 py-4 border-2 border-[#F5A623] transition-all shadow-[4px_4px_0px_0px_rgba(245,166,35,0.4)] hover:shadow-[6px_6px_0px_0px_rgba(245,166,35,0.6)] hover:-translate-y-0.5"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Submitting…
                    </>
                  ) : (
                    <>
                      <Send size={16} /> Sign me up
                    </>
                  )}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </main>

      <footer className="border-t border-white/10 py-6 px-6 sm:px-10">
        <p className="text-center text-xs uppercase tracking-[0.2em] text-[#FDFBF7]/40">
          © {new Date().getFullYear()} The Bar — Colac · Great Drinks · Great Times · Great People
        </p>
      </footer>
    </div>
  );
}

// -------- small inputs --------
const Field = ({ label, testid, icon, ...props }) => (
  <label className="block">
    <span className="block text-[10px] uppercase tracking-[0.2em] text-[#FDFBF7]/60 font-semibold mb-2">
      {label}
      {props.required && <span className="text-[#F5A623] ml-1">*</span>}
    </span>
    <div className="relative">
      <input
        {...props}
        data-testid={testid}
        className={`w-full bg-black border border-white/15 focus:border-[#F5A623] outline-none px-4 py-3 text-[#FDFBF7] placeholder:text-[#FDFBF7]/25 transition-colors ${icon ? "pl-10" : ""}`}
      />
      {icon && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
          {icon}
        </span>
      )}
    </div>
  </label>
);
